"use client";

import { useMemo, useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { defaultLocale, formatPrice, isValidLocale, type Locale } from "@/config/locale";
import { buildResolvedMoneyKey, type ResolvedMoneyPresentation } from "@/lib/pricing/resolve-display-money";
import { normalizePackageName, parsePackageCount, parseVariantName } from "@/lib/pricing/variant-pack-math";
import { ReferencePrimaryPrice } from "@/ui/components/pricing/reference-primary-price";
import { isPurchasableVariant, sortVariantsByStrengthAndPackage } from "@/lib/packaging/variant-order";
import { cn } from "@/lib/utils";

/**
 * Fallback selector for variants that have no structured attributes.
 *
 * Used when variants only have names (e.g., "Navy blue S", "Navy blue M")
 * but no attribute data for grouping by Color/Size/etc.
 *
 * ## When this is used
 *
 * - Variants have empty `attributes` arrays
 * - Product has multiple variants but no attribute-based grouping possible
 *
 * ## Limitations vs. structured attributes
 *
 * - No color swatches or visual differentiation
 * - No cross-filtering (can't gray out incompatible options)
 * - Combinatorial explosion for products with many variants
 * - Inconsistent UX compared to attribute-based selectors
 *
 * Consider configuring proper variant attributes in Saleor Dashboard
 * for a better customer experience.
 */

interface VariantNameSelectorProps {
	variants: Array<{
		id: string;
		name: string;
		quantityAvailable?: number | null;
		pricing?: {
			price?: { gross: { amount: number; currency: string } } | null;
			priceUndiscounted?: { gross: { amount: number; currency: string } } | null;
		} | null;
	}>;
	selectedVariantId?: string;
	onSelect: (variantId: string) => void;
	label?: string;
	/** Whether a transition is in progress */
	isPending?: boolean;
	locale?: string;
	channel?: string;
	resolvedByVariantId?: Record<string, ResolvedMoneyPresentation>;
	/** Pre-resolved keys for per-unit and bundle savings (see buildPdpResolvedPrices). */
	resolvedMoneyByKey?: Record<string, ResolvedMoneyPresentation>;
}

type ParsedVariant = {
	id: string;
	name: string;
	dosage: string;
	packageName: string;
	packageCount?: number;
	quantityAvailable: number | null;
	available: boolean;
	price?: { amount: number; currency: string };
};

/** Map package labels like "2 Pack", "3 Vials" to localized display strings (e.g. "2 Stück") */
function formatPackageLabel(packageName: string, pieceLabel: string): string {
	const match = packageName.match(
		/^(\d+)\s*(?:Pack(?:ung)?|Vials?|Stück|Pieces?|Unidad(?:es)?|Pi[eè]ces?|Pezz[oi]|Stuks?|Unidades?|Sztuk[ai]?|Szt\.?)$/i,
	);
	if (match) {
		return `${match[1]} ${pieceLabel}`;
	}
	return packageName;
}

export function VariantNameSelector({
	variants,
	selectedVariantId,
	onSelect,
	label = "Variant",
	isPending,
	locale,
	channel,
	resolvedByVariantId,
	resolvedMoneyByKey,
}: VariantNameSelectorProps) {
	const t = useTranslations("pdpUx");
	const loc = locale && isValidLocale(locale) ? (locale as Locale) : defaultLocale;
	const fmt = (amount: number, currency: string) => formatPrice(amount, currency, loc);
	const orderedVariants = useMemo(() => sortVariantsByStrengthAndPackage(variants), [variants]);
	const selectedVariant =
		orderedVariants.find((variant) => variant.id === selectedVariantId) ??
		orderedVariants.find(isPurchasableVariant);
	const showPrices = true;
	const parsedVariants = useMemo<ParsedVariant[]>(() => {
		const parsed: ParsedVariant[] = [];
		for (const variant of orderedVariants) {
			const parsedName = parseVariantName(variant.name);
			if (!parsedName) continue;
			parsed.push({
				id: variant.id,
				name: variant.name,
				dosage: parsedName.dosage,
				packageName: parsedName.packageName,
				packageCount: parsePackageCount(parsedName.packageName),
				quantityAvailable: variant.quantityAvailable ?? null,
				available: isPurchasableVariant(variant),
				price: variant.pricing?.price?.gross
					? {
							amount: variant.pricing.price.gross.amount,
							currency: variant.pricing.price.gross.currency,
						}
					: undefined,
			});
		}
		return parsed;
	}, [orderedVariants]);

	const canUseStructuredFallback = parsedVariants.length > 0 && parsedVariants.length === variants.length;
	const selectedParsed = parsedVariants.find((variant) => variant.id === selectedVariant?.id);
	const selectedDosage = selectedParsed?.dosage;
	const selectedPackage = selectedParsed?.packageName;

	const dosageOptions = useMemo(() => {
		const map = new Map<string, { available: boolean }>();
		for (const variant of parsedVariants) {
			const existing = map.get(variant.dosage);
			map.set(variant.dosage, {
				available: Boolean(existing?.available || variant.available),
			});
		}
		return [...map.entries()].map(([dosage, info]) => ({ dosage, ...info }));
	}, [parsedVariants]);

	const packageOptions = (() => {
		if (!selectedDosage) return [];
		const filteredVariants = parsedVariants.filter((variant) => variant.dosage === selectedDosage);

		const singleVariant = filteredVariants.find((v) => {
			const parsed = parseVariantName(v.name);
			return parsed && parsed.dosage === selectedDosage && parsePackageCount(parsed.packageName) === 1;
		});
		const singlePrice = singleVariant?.price?.amount ?? 0;

		return filteredVariants.map((option) => {
			const variantPrice = option.price?.amount ?? 0;
			const packCount = parsePackageCount(option.packageName);
			const savingsAmount = packCount > 1 ? singlePrice * packCount - variantPrice : 0;
			const hasBundleDiscount = savingsAmount > 0.009;

			return {
				...option,
				packCount,
				savingsAmount: hasBundleDiscount ? savingsAmount : undefined,
			};
		});
	})();

	const selectedCompleteVariant = selectedParsed;

	// Auto-select variant on mount when no variant is pre-selected
	const hasAutoSelected = useRef(false);
	useEffect(() => {
		if (hasAutoSelected.current || variants.some((variant) => variant.id === selectedVariantId)) return;
		if (!selectedVariant || !isPurchasableVariant(selectedVariant)) return;
		hasAutoSelected.current = true;
		onSelect(selectedVariant.id);
	}, [selectedVariantId, selectedVariant, onSelect, variants]);

	if (canUseStructuredFallback) {
		return (
			<div className="space-y-3 sm:space-y-5">
				<div className="space-y-2">
					<div className="text-sm font-medium">{t("variantDosage")}</div>
					<div
						role="group"
						aria-label={t("variantDosage")}
						className={cn(
							"flex flex-wrap gap-2 transition-opacity duration-150",
							isPending && "pointer-events-none opacity-60",
						)}
					>
						{dosageOptions.map((option) => {
							const isSelected = selectedDosage === option.dosage;
							return (
								<button
									key={option.dosage}
									type="button"
									onClick={() => {
										const candidates = parsedVariants.filter(
											(variant) => variant.dosage === option.dosage && variant.available,
										);
										const next =
											candidates.find(
												(variant) =>
													normalizePackageName(variant.packageName) ===
													normalizePackageName(selectedPackage ?? ""),
											) ?? candidates[0];
										if (next) onSelect(next.id);
									}}
									aria-pressed={isSelected}
									disabled={!option.available || isPending}
									className={cn(
										"min-h-10 rounded-lg border px-3 text-sm font-medium transition-all sm:min-h-12 sm:px-4",
										"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
										isSelected
											? "border-foreground bg-foreground text-background"
											: "border-gray-400 bg-background text-foreground hover:border-foreground",
										!option.available && "cursor-not-allowed line-through opacity-60",
									)}
								>
									{option.dosage}
								</button>
							);
						})}
					</div>
				</div>

				<div className="space-y-2">
					<div className="text-sm font-medium">{t("variantQuantity")}</div>
					<div
						role="group"
						aria-label={t("variantQuantity")}
						className={cn(
							"flex flex-wrap gap-2 transition-opacity duration-150",
							isPending && "pointer-events-none opacity-60",
						)}
					>
						{packageOptions.map((option) => {
							const isSelected =
								selectedCompleteVariant?.id === option.id ||
								(selectedPackage &&
									normalizePackageName(selectedPackage) === normalizePackageName(option.packageName));
							const isOutOfStock = !option.available;
							const variantPrice = option.price?.amount ?? 0;
							const currency = option.price?.currency ?? "";
							const unitKey =
								option.packCount > 1 && currency
									? buildResolvedMoneyKey(variantPrice / option.packCount, currency)
									: "";
							const unitPres = unitKey ? resolvedMoneyByKey?.[unitKey] : undefined;
							const savKey =
								option.savingsAmount && option.savingsAmount > 0 && option.price
									? buildResolvedMoneyKey(option.savingsAmount, option.price.currency)
									: "";
							const savPres = savKey ? resolvedMoneyByKey?.[savKey] : undefined;

							return (
								<div key={option.id} className="relative">
									<button
										type="button"
										onClick={() => {
											onSelect(option.id);
										}}
										aria-pressed={Boolean(isSelected)}
										disabled={isOutOfStock || isPending}
										className={cn(
											"min-h-10 rounded-lg border px-3 py-2 text-left text-sm font-medium transition-all sm:min-h-12 sm:px-4",
											isSelected
												? "border-foreground bg-foreground text-background"
												: "border-gray-400 bg-background text-foreground hover:border-foreground",
											isOutOfStock && "cursor-not-allowed line-through opacity-60",
										)}
									>
										<span className="flex flex-col items-start leading-tight">
											<span>{formatPackageLabel(option.packageName, t("variantPiece"))}</span>
											{option.price && (
												<span
													className={cn(
														"text-xs",
														isSelected ? "text-background/80" : "text-muted-foreground",
													)}
												>
													{resolvedByVariantId?.[option.id] && locale && channel ? (
														<ReferencePrimaryPrice
															presentation={resolvedByVariantId[option.id]!}
															locale={locale}
															channel={channel}
															size="sm"
															showReferenceTooltip={false}
															className={cn(isSelected && "text-background/80")}
														/>
													) : (
														fmt(option.price.amount, option.price.currency)
													)}
												</span>
											)}
											{option.packCount > 1 && option.price && (
												<span
													className={cn(
														"text-xs",
														isSelected ? "text-background/70" : "text-muted-foreground",
													)}
												>
													{unitPres && locale && channel ? (
														<>
															<ReferencePrimaryPrice
																presentation={unitPres}
																locale={locale}
																channel={channel}
																size="sm"
																showReferenceTooltip={false}
																className={cn(isSelected && "text-background/80")}
															/>
															/{t("variantPiece")}
														</>
													) : (
														<>
															{fmt(variantPrice / option.packCount, currency)}/{t("variantPiece")}
														</>
													)}
												</span>
											)}
										</span>
									</button>
									{option.savingsAmount && option.price && (
										<span
											className="pointer-events-none absolute -bottom-2 -right-1 rounded-full border border-green-600 bg-background px-1.5 py-0.5 text-[10px] font-semibold text-green-600"
											aria-hidden="true"
										>
											{t("variantSave", {
												amount:
													savPres && locale && channel
														? savPres.primaryLabel
														: fmt(option.savingsAmount, option.price.currency),
											})}
										</span>
									)}
								</div>
							);
						})}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-3">
			<div className="flex items-center gap-2">
				<span className="text-sm font-medium">{label}</span>
				{selectedVariant && <span className="text-sm text-muted-foreground">{selectedVariant.name}</span>}
			</div>

			<div
				role="group"
				aria-label={label}
				aria-busy={isPending}
				className={cn(
					"flex flex-wrap gap-2 transition-opacity duration-150",
					isPending && "pointer-events-none opacity-60",
				)}
				style={{ transitionDelay: isPending ? "100ms" : "0ms" }}
			>
				{orderedVariants.map((variant) => {
					const isSelected = variant.id === selectedVariant?.id;
					const isOutOfStock = !isPurchasableVariant(variant);
					const price = variant.pricing?.price?.gross;
					const undiscountedPrice = variant.pricing?.priceUndiscounted?.gross;
					const hasDiscount = price && undiscountedPrice && undiscountedPrice.amount > price.amount;
					const discountPercent = hasDiscount
						? Math.round((1 - price.amount / undiscountedPrice.amount) * 100)
						: null;

					// Build accessible label
					const pres = price && resolvedByVariantId?.[variant.id];
					const priceLabel =
						showPrices && price
							? pres && locale && channel
								? pres.primaryLabel
								: fmt(price.amount, price.currency)
							: null;

					const accessibleParts = [
						variant.name,
						isOutOfStock && "out of stock",
						priceLabel,
						discountPercent && `${discountPercent}% off`,
					].filter(Boolean);

					return (
						<div key={variant.id} className="relative">
							<button
								type="button"
								onClick={() => onSelect(variant.id)}
								disabled={isOutOfStock || isPending}
								aria-disabled={isOutOfStock || isPending}
								className={cn(
									"min-h-10 min-w-[4.5rem] rounded-lg border px-3 py-2 text-sm font-medium transition-all sm:min-h-12 sm:px-4",
									"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
									isSelected
										? "border-foreground bg-foreground text-background"
										: "border-border bg-background text-foreground hover:border-foreground",
									isOutOfStock && "cursor-not-allowed text-muted-foreground line-through opacity-60",
								)}
								title={isOutOfStock ? `${variant.name} - Out of stock` : undefined}
								aria-label={accessibleParts.join(", ")}
								aria-pressed={isSelected}
							>
								<span className="flex items-center gap-2">
									{variant.name}
									{showPrices && price && (
										<span className={cn("text-xs", isSelected ? "opacity-80" : "text-muted-foreground")}>
											{resolvedByVariantId?.[variant.id] && locale && channel ? (
												<ReferencePrimaryPrice
													presentation={resolvedByVariantId[variant.id]!}
													locale={locale}
													channel={channel}
													size="sm"
													showReferenceTooltip={false}
													className={cn(isSelected && "opacity-80")}
												/>
											) : (
												fmt(price.amount, price.currency)
											)}
										</span>
									)}
								</span>
							</button>
							{discountPercent && !isOutOfStock && (
								<span
									className="pointer-events-none absolute -bottom-2 -right-1 rounded-full border border-destructive bg-background px-1.5 py-0.5 text-[10px] font-semibold text-destructive"
									aria-hidden="true"
								>
									-{discountPercent}%
								</span>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}
